import { NdcAwardKind } from '@dr-pam/common-types/database/enums';

/** The award certificates a member can download; the value is the URL segment. */
export enum NdcCertificateKind {
	ACCREDITATION = 'accreditation',
	FELLOWSHIP = 'fellowship',
	PROVIDERS_EDUCATORS = 'providers-educators',
}

/** The Maintenance licences a member can download; the value is the URL segment. Fellowship has none. */
export enum NdcLicenceKind {
	ACCREDITATION = 'accreditation-licence',
	PROVIDERS_EDUCATORS = 'providers-educators-licence',
}

/** The certificate each award earns. */
export const NDC_AWARD_CERTIFICATES: Record<NdcAwardKind, NdcCertificateKind> = {
	[NdcAwardKind.ACCREDITATION]: NdcCertificateKind.ACCREDITATION,
	[NdcAwardKind.FELLOWSHIP]: NdcCertificateKind.FELLOWSHIP,
	[NdcAwardKind.PROVIDERS_EDUCATORS]: NdcCertificateKind.PROVIDERS_EDUCATORS,
};

/** The Maintenance licence each award can hold; only these have a licence design. */
export const NDC_AWARD_LICENCES: Partial<Record<NdcAwardKind, NdcLicenceKind>> = {
	[NdcAwardKind.ACCREDITATION]: NdcLicenceKind.ACCREDITATION,
	[NdcAwardKind.PROVIDERS_EDUCATORS]: NdcLicenceKind.PROVIDERS_EDUCATORS,
};

/** The award whose Maintenance, and its agreement, keeps each award current; Fellowship has no Maintenance of its own. */
export const NDC_AWARD_MAINTAINED_BY: Record<NdcAwardKind, NdcAwardKind> = {
	[NdcAwardKind.ACCREDITATION]: NdcAwardKind.ACCREDITATION,
	[NdcAwardKind.FELLOWSHIP]: NdcAwardKind.ACCREDITATION,
	[NdcAwardKind.PROVIDERS_EDUCATORS]: NdcAwardKind.PROVIDERS_EDUCATORS,
};

type Named = { id: string; name: string };

/** One award's configuration, by name, as an admin sees it; an award not set up yet has no programme. */
export type NdcAwardConfigurationView = {
	kind: NdcAwardKind;
	programme: Named | null;
	examQuiz: Named | null;
	maintenanceProduct: Named | null;
	maintenanceAgreementQuiz: Named | null;
	/** Off hides the award's certificate from every member while it is being finalised. */
	isCertificateEnabled: boolean;
	/** What no longer holds together, such as an exam taken out of the programme since it was chosen. */
	problems: string[];
};

/** Anything a member can download. */
export type NdcDocumentKind = NdcCertificateKind | NdcLicenceKind;

/** The kind a URL segment names, or null. */
export function parseNdcDocumentKind(value: string): NdcDocumentKind | null {
	const kinds: string[] = [...Object.values(NdcCertificateKind), ...Object.values(NdcLicenceKind)];
	return kinds.includes(value) ? (value as NdcDocumentKind) : null;
}

export function isNdcCertificateKind(kind: NdcDocumentKind): kind is NdcCertificateKind {
	return Object.values<string>(NdcCertificateKind).includes(kind);
}

/** Dated as in NdcAwardCompletion: by `completedOn` when an admin entered one, else by `completedAt`. */
export type NdcHeldCertificate = {
	kind: NdcCertificateKind;
	completedOn: string | null;
	completedAt: string | null;
	/** While a current Maintenance licence waits for its agreement, the certificate is listed but cannot be downloaded. */
	isAgreed: boolean;
};

export type NdcHeldLicence = {
	kind: NdcLicenceKind;
	yearsHeld: number;
	/** ISO instants. */
	currentFrom: string;
	currentTo: string | null;
	/** A lapsed licence is listed but cannot be downloaded. */
	isCurrent: boolean;
	/** Until the Maintenance agreement is passed, a current licence is listed but cannot be downloaded. */
	isAgreed: boolean;
};

/** What the logged-in member can download. */
export type NdcCertificateHoldings = {
	certificates: NdcHeldCertificate[];
	licences: NdcHeldLicence[];
};

/**
 * The account's full name as printed on a certificate: stray spacing collapsed, and the invisible
 * characters that reorder text or print as nothing removed. Joiners that scripts such as Persian
 * need are kept.
 */
export function certificateName(fullName: string) {
	return fullName
		.replace(/[\u200B\u200E\u200F\u202A-\u202E\u2060\u2066-\u2069\uFEFF]/g, '')
		.trim()
		.replace(/\s+/g, ' ');
}
